/**
 * Employee Detail Page
 *
 * HR and Admin view for viewing and editing employee details.
 */

import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Layout } from '../../components/Layout';
import {
  useEmployee,
  useUpdateEmployee,
  useDeleteEmployee,
} from './useEmployees';
import { useFunctions, useDepartments } from '../settings/useSettings';
import {
  useEmployeeAssignedTasks,
  useEmployeeAvailableTasks,
  useAssignTask,
  useRemoveTask,
} from './useEmployeeTasks';
import { useCurrentPackage, useTaskPeriods } from './useAssignmentPeriods';
import { CurrentPackageCard } from './components/CurrentPackageCard';
import { PackageChangeDialog } from './components/PackageChangeDialog';
import { PeriodTimeline } from './components/PeriodTimeline';
import { NiveauOverrideSection } from './NiveauOverrideSection';
import { DienstverbandSection } from './DienstverbandSection';
import { competenciesApi, type Competency, type EmployeeCompetencies } from '../../lib/competenciesApi';
import { rolesApi, type Role, type UpdateEmployeeInput } from '../../lib/employeesApi';
import { matchesSearch } from '../../lib/textUtils';
import { useAuth } from '../auth/AuthContext';
import { EmployeeScheduleTab } from '../werkrooster/components/EmployeeScheduleTab';
import { NmbrsOverridePanel } from '../nmbrs/components/NmbrsOverridePanel';
import { PlanbaarheidVeld } from '../planning/components/PlanbaarheidVeld';

export function EmployeeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const employeeId = Number(id);

  const { hasRole } = useAuth();

  const { data: employee, isLoading, error } = useEmployee(employeeId);
  const updateEmployee = useUpdateEmployee();
  const deleteEmployee = useDeleteEmployee();

  // Task assignment hooks
  const { data: assignedTasks, isLoading: tasksLoading } = useEmployeeAssignedTasks(employeeId);
  const { data: availableTasks } = useEmployeeAvailableTasks(employeeId);
  const assignTask = useAssignTask();
  const removeTask = useRemoveTask();

  // Kerntaken (Epic 3, Story 3.2) — huidige tab bestaat NAAST de bestaande
  // "Taken"-tab tijdens de overgang (Epic 6 stuurt de zichtbaarheid op
  // scoring_model/core_tasks_active), zie story Dev Notes.
  const { currentPackage, isLoading: currentPackageLoading } = useCurrentPackage(employeeId);
  const { data: taskPeriods } = useTaskPeriods(employeeId);
  const [isPackageDialogOpen, setIsPackageDialogOpen] = useState(false);

  // Competency assignment hooks
  const { data: employeeCompetencies, isLoading: competenciesLoading } = useQuery<EmployeeCompetencies>({
    queryKey: ['competencies', 'employee', employeeId],
    queryFn: () => competenciesApi.getForEmployee(employeeId),
    enabled: !!employeeId,
  });

  const { data: availableBasisCompetencies } = useQuery<Competency[]>({
    queryKey: ['competencies', 'basis'],
    queryFn: () => competenciesApi.getBasis(true),
  });

  // Functions and Departments for dropdowns
  const { data: functions } = useFunctions();
  const { data: departments } = useDepartments();

  const assignCompetency = useMutation({
    mutationFn: (competencyId: number) => competenciesApi.assignToEmployee(employeeId, competencyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['competencies', 'employee', employeeId] });
    },
  });

  const removeCompetency = useMutation({
    mutationFn: (competencyId: number) => competenciesApi.removeFromEmployee(employeeId, competencyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['competencies', 'employee', employeeId] });
    },
  });

  // Role update mutation
  const updateRole = useMutation({
    mutationFn: (role: Role) => rolesApi.updateRole(employee?.userId ?? 0, { role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee', employeeId] });
    },
  });

  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showTaskAssignment, setShowTaskAssignment] = useState(false);
  const [showCompetencyAssignment, setShowCompetencyAssignment] = useState(false);
  const [taskFilter, setTaskFilter] = useState('');
  const [activeTab, setActiveTab] = useState<'taken' | 'kerntaken' | 'competenties' | 'werkrooster'>('taken');
  const canEditSchedule = hasRole(['admin', 'hr', 'beoordelaar']);
  const [competencyFilter, setCompetencyFilter] = useState('');

  // Form state
  const [formData, setFormData] = useState<UpdateEmployeeInput>({});
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);

  // Initialize form when employee loads
  if (employee && Object.keys(formData).length === 0) {
    setFormData({
      functie: employee.functie,
      team: employee.team,
      startdatum: employee.startdatum,
    });
  }

  // Initialize selected role when employee loads
  if (employee && selectedRole === null) {
    setSelectedRole(employee.role as Role);
  }

  async function handleSave() {
    if (!employee) return;

    try {
      // Update employee profile
      await updateEmployee.mutateAsync({ id: employee.id, input: formData });

      // Update role if changed
      if (selectedRole && selectedRole !== employee.role) {
        await updateRole.mutateAsync(selectedRole);
      }

      setIsEditing(false);
    } catch {
      // Error is handled by mutation state
    }
  }

  async function handleDelete() {
    if (!employee) return;

    try {
      await deleteEmployee.mutateAsync(employee.id);
      navigate('/medewerkers');
    } catch {
      // Error is handled by mutation state
    }
  }

  async function handleAssignTask(taskId: number) {
    if (!employee) return;

    try {
      await assignTask.mutateAsync({ employeeId: employee.id, taskId });
    } catch {
      // Error is handled by mutation state
    }
  }

  async function handleRemoveTask(taskId: number) {
    if (!employee) return;

    // Een gearchiveerde koppeling is praktisch gesproken niet terug te zetten:
    // `assignTaskToEmployee` weigert een inactieve taak, dus dit scherm biedt
    // geen weg terug. Bovendien verdwijnt de taak uit een lopende
    // beoordelingsronde. Daarom eerst bevestigen -- bij een actieve taak niet,
    // die is gewoon opnieuw toe te wijzen.
    // Spec: _bmad-output/implementation-artifacts/spec-weergave-actief-puntentotaal.md
    const task = assignedTasks?.tasks.find((t) => t.id === taskId);
    if (task && task.isActief === false) {
      const bevestigd = window.confirm(
        `"${task.name}" is een gearchiveerde taak. Verwijderen kan niet via dit scherm ongedaan worden gemaakt: een gearchiveerde taak is niet opnieuw toe te wijzen. Weet u zeker dat u de koppeling wilt verwijderen?`
      );
      if (!bevestigd) return;
    }

    try {
      await removeTask.mutateAsync({ employeeId: employee.id, taskId });
    } catch {
      // Error is handled by mutation state
    }
  }

  async function handleAssignCompetency(competencyId: number) {
    try {
      await assignCompetency.mutateAsync(competencyId);
    } catch {
      // Error is handled by mutation state
    }
  }

  async function handleRemoveCompetency(competencyId: number) {
    try {
      await removeCompetency.mutateAsync(competencyId);
    } catch {
      // Error is handled by mutation state
    }
  }

  // Get unassigned basis competencies (for assignment modal)
  const unassignedBasisCompetencies = availableBasisCompetencies?.filter(
    (comp) => !employeeCompetencies?.basis.some((assigned) => assigned.id === comp.id)
  ) ?? [];

  // Lijsten houden alles, tellers tellen actief. `assignedTasks.tasks` blijft
  // dus compleet -- de takenlijst hieronder markeert het gearchiveerde -- maar
  // de tabbadge en de statistieken in de zijbalk rekenen over deze
  // deelverzameling, net als `assignedTasks.totalWeging` aan de serverkant.
  // Spec: _bmad-output/implementation-artifacts/spec-weergave-actief-puntentotaal.md
  const activeAssignedTasks = assignedTasks?.tasks.filter((t) => t.isActief !== false);

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Laden...</span>
        </div>
      </Layout>
    );
  }

  if (error || !employee) {
    return (
      <Layout>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <svg className="mx-auto h-12 w-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-red-900">
            Medewerker niet gevonden
          </h3>
          <p className="mt-2 text-sm text-red-700">
            De gevraagde medewerker bestaat niet of u heeft geen toegang.
          </p>
          <Link
            to="/medewerkers"
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            Terug naar overzicht
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="employee-detail-page">
        {/* Breadcrumb */}
        <nav className="mb-6">
          <ol className="flex items-center space-x-2 text-sm text-gray-500">
            <li>
              <Link to="/medewerkers" className="hover:text-gray-700">
                Medewerkers
              </Link>
            </li>
            <li>
              <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </li>
            <li className="text-gray-900 font-medium">{employee.name}</li>
          </ol>
        </nav>

        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center">
            <div className="flex-shrink-0 h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
              <span className="text-2xl font-bold text-blue-600">
                {employee.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="ml-4">
              <h1 className="text-2xl font-bold text-gray-900">{employee.name}</h1>
              <p className="text-sm text-gray-500">{employee.email}</p>
            </div>
          </div>
          <div className="mt-4 sm:mt-0 flex space-x-3">
            {isEditing ? (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  disabled={updateEmployee.isPending}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleSave}
                  disabled={updateEmployee.isPending}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {updateEmployee.isPending && (
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  )}
                  Opslaan
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => window.open(`/medewerkers/${employee.id}/print`, '_blank')}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Afdrukken
                </button>
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Bewerken
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Verwijderen
                </button>
              </>
            )}
          </div>
        </div>

        {/* Error Messages */}
        {updateEmployee.error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-700">
              Fout bij het opslaan. Probeer het opnieuw.
            </p>
          </div>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Employee Details */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Medewerker Informatie
              </h2>

              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Niveau</dt>
                  <dd className="mt-1">
                    <NiveauOverrideSection employeeId={employee.id} employeeNiveauName={employee.niveau.name} />
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">Rol</dt>
                  <dd className="mt-1">
                    {isEditing ? (
                      <select
                        value={selectedRole ?? employee.role}
                        onChange={(e) => setSelectedRole(e.target.value as Role)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      >
                        <option value="medewerker">Medewerker</option>
                        <option value="beoordelaar">Beoordelaar</option>
                        <option value="hr">HR</option>
                        <option value="admin">Admin</option>
                      </select>
                    ) : (
                      <span className="text-sm text-gray-900 capitalize">{employee.role}</span>
                    )}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">Functie</dt>
                  <dd className="mt-1">
                    {isEditing ? (
                      <select
                        value={formData.functie ?? ''}
                        onChange={(e) => setFormData({ ...formData, functie: e.target.value || null })}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      >
                        <option value="">Selecteer functie...</option>
                        {functions?.map((func) => (
                          <option key={func.id} value={func.name}>
                            {func.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-sm text-gray-900">{employee.functie || '-'}</span>
                    )}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">Team</dt>
                  <dd className="mt-1">
                    {isEditing ? (
                      <select
                        value={formData.team ?? ''}
                        onChange={(e) => setFormData({ ...formData, team: e.target.value || null })}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      >
                        <option value="">Selecteer team...</option>
                        {departments?.map((dept) => (
                          <option key={dept.id} value={dept.name}>
                            {dept.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-sm text-gray-900">{employee.team || '-'}</span>
                    )}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">Startdatum</dt>
                  <dd className="mt-1">
                    {isEditing ? (
                      <input
                        type="date"
                        value={formData.startdatum ?? ''}
                        onChange={(e) => setFormData({ ...formData, startdatum: e.target.value || null })}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      />
                    ) : (
                      <span className="text-sm text-gray-900">
                        {employee.startdatum
                          ? new Date(employee.startdatum).toLocaleDateString('nl-NL')
                          : '-'}
                      </span>
                    )}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="mt-1">
                    {/*
                      Review-fix (Story 5.2): uit dienst neemt voorrang op de
                      MySQL-account-status -- anders toont deze badge "Actief"
                      (groen) vlak boven de "Uit dienst per [datum]"-chip in
                      de Dienstverband-sectie, een verwarrend/tegenstrijdig
                      signaal precies op het moment dat dit het duidelijkst
                      moet zijn.
                    */}
                    {employee.uitDienstDatum ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Uit dienst
                      </span>
                    ) : (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        employee.active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {employee.active ? 'Actief' : 'Inactief'}
                      </span>
                    )}
                  </dd>
                </div>

                {employee.manager && (
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-500">Manager</dt>
                    <dd className="mt-1 text-sm text-gray-900">{employee.manager.name}</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Dienstverband (Epic 5, Story 5.2, AD-7/UX-DR6) */}
            <DienstverbandSection
              employeeId={employee.id}
              employeeName={employee.name}
              uitDienstDatum={employee.uitDienstDatum}
              canRevertUitDienst={employee.canRevertUitDienst}
            />

            {/* Nmbrs Override Panel - shown only when employee is linked to Nmbrs (Story 2.3) */}
            <NmbrsOverridePanel employeeId={employeeId} />

            {/* Planbaarheid — veld bij de medewerker, GEEN apart beheerscherm (Epic D-1, Story D-1.4, UX §5.3) */}
            <PlanbaarheidVeld employeeId={employeeId} />

            {/* Tasks & Competencies with Tabs */}
            <div className="bg-white shadow-sm border border-gray-200 rounded-lg">
              {/* Tab Navigation */}
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex" aria-label="Tabs">
                  <button
                    onClick={() => setActiveTab('taken')}
                    className={`flex-1 py-4 px-1 text-center border-b-2 font-medium text-sm ${
                      activeTab === 'taken'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                      Taken
                      <span className="ml-1 bg-gray-100 text-gray-900 py-0.5 px-2 rounded-full text-xs">
                        {activeAssignedTasks?.length ?? employee.tasks.length}
                      </span>
                    </div>
                  </button>
                  <button
                    data-testid="employee-detail-tab-kerntaken"
                    onClick={() => setActiveTab('kerntaken')}
                    className={`flex-1 py-4 px-1 text-center border-b-2 font-medium text-sm ${
                      activeTab === 'kerntaken'
                        ? 'border-[#2F5A7A] text-[#2F5A7A]'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Kerntaken
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('competenties')}
                    className={`flex-1 py-4 px-1 text-center border-b-2 font-medium text-sm ${
                      activeTab === 'competenties'
                        ? 'border-purple-500 text-purple-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                      </svg>
                      Competenties
                      <span className="ml-1 bg-gray-100 text-gray-900 py-0.5 px-2 rounded-full text-xs">
                        {(employeeCompetencies?.basis.length ?? 0) + (employeeCompetencies?.vak.length ?? 0)}
                      </span>
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('werkrooster')}
                    className={`flex-1 py-4 px-1 text-center border-b-2 font-medium text-sm ${
                      activeTab === 'werkrooster'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Werkrooster
                    </div>
                  </button>
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {/* Tasks Tab */}
                {activeTab === 'taken' && (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-medium text-gray-900">
                        Taken
                      </h2>
                      <button
                        onClick={() => setShowTaskAssignment(true)}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <svg className="mr-1.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Taak toewijzen
                      </button>
                    </div>

                    {tasksLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                        <span className="ml-2 text-sm text-gray-500">Laden...</span>
                      </div>
                    ) : (assignedTasks?.tasks.length ?? 0) > 0 ? (
                      <ul className="divide-y divide-gray-200">
                        {assignedTasks?.tasks.map((task) => {
                          // Gearchiveerde koppeling: blijft staan (archiveren
                          // verwijdert niets en de beoordelingsflow leest deze
                          // lijst nog), maar telt niet mee in het puntentotaal
                          // hieronder. Dat moet zichtbaar zijn, anders lijkt de
                          // som niet te kloppen met de lijst.
                          const isGearchiveerd = task.isActief === false;
                          return (
                          <li
                            key={task.id}
                            data-testid={`employee-task-row-${task.id}`}
                            className={`py-3 flex items-center justify-between group ${isGearchiveerd ? 'opacity-60' : ''}`}
                          >
                            <div>
                              <span className="text-sm text-gray-900">{task.name}</span>
                              {isGearchiveerd && (
                                <span className="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                                  Gearchiveerd &ndash; telt niet mee
                                </span>
                              )}
                              {task.beschrijving && (
                                <p className="text-xs text-gray-500 mt-0.5 truncate max-w-md">
                                  {task.beschrijving}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className={`text-sm ${isGearchiveerd ? 'text-gray-400 line-through' : 'text-gray-500'}`}>
                                {task.weging} punten
                              </span>
                              <button
                                onClick={() => handleRemoveTask(task.id)}
                                disabled={removeTask.isPending}
                                className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-600 transition-opacity"
                                title="Verwijderen"
                                aria-label={`Taak ${task.name} verwijderen`}
                              >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-500">Geen taken toegewezen.</p>
                    )}

                    {assignedTasks && (
                      <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
                        <span className="text-sm text-gray-500">Totaal punten</span>
                        <span className="text-sm font-medium text-gray-900">
                          {assignedTasks.totalWeging} punten
                        </span>
                      </div>
                    )}
                  </>
                )}

                {/* Kerntaken Tab (Epic 3, Story 3.2 + 3.3) */}
                {activeTab === 'kerntaken' && (
                  <>
                    {/*
                      Epic 5 (Story 5.1/5.2, AD-7) review-fix: een uit-dienst-
                      medewerker mag geen nieuw tijdvak meer krijgen -- dat zou
                      precies de toestand terugbrengen die uit-dienst juist
                      moest beëindigen ("kerntaak-toewijzingen eindigen", de
                      gevolgen-lijst in UitDienstDialog). De server weigert dit
                      inmiddels ook (assignmentService.assertEmployeeExists →
                      EMPLOYEE_UIT_DIENST), maar de knop hier al verbergen
                      voorkomt een verwarrende foutmelding en is consistent met
                      de UI die de gevolgen vooraf belooft.

                      Code-review-fix (tech-spec actief-definitie, CAP-3): de
                      server weigert sindsdien OOK een medewerker met een
                      uitgezette login maar zónder uit-dienst-datum
                      (EMPLOYEE_INACTIEF). Alleen op `uitDienstDatum` toetsen
                      liet precies dat geval hier open: de knop bleef klikbaar
                      en de gebruiker kreeg pas bij opslaan een rauwe 409 — de
                      verwarrende foutmelding die dit blok juist wilde
                      voorkomen. De voorwaarde volgt nu dezelfde canonieke
                      regel als de backend.
                    */}
                    <CurrentPackageCard
                      currentPackage={currentPackage}
                      isLoading={currentPackageLoading}
                      readOnly={!!employee.uitDienstDatum || !employee.active}
                      onChangePackage={() => setIsPackageDialogOpen(true)}
                    />
                    <PeriodTimeline periods={taskPeriods ?? []} />
                    <PackageChangeDialog
                      isOpen={isPackageDialogOpen}
                      onClose={() => setIsPackageDialogOpen(false)}
                      employeeId={employee.id}
                      currentPackage={currentPackage}
                    />
                  </>
                )}

                {/* Werkrooster Tab */}
                {activeTab === 'werkrooster' && (
                  <EmployeeScheduleTab
                    employeeId={employee.id}
                    canEdit={canEditSchedule}
                  />
                )}

                {/* Competencies Tab */}
                {activeTab === 'competenties' && (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-medium text-gray-900">
                        Competenties
                      </h2>
                      <button
                        onClick={() => setShowCompetencyAssignment(true)}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                      >
                        <svg className="mr-1.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Competentie toewijzen
                      </button>
                    </div>

                    {competenciesLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
                        <span className="ml-2 text-sm text-gray-500">Laden...</span>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Basis Competencies */}
                        <div>
                          <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                            Basiscompetenties ({employeeCompetencies?.basis.length ?? 0})
                          </h3>
                          {(employeeCompetencies?.basis.length ?? 0) > 0 ? (
                            <ul className="divide-y divide-gray-200">
                              {employeeCompetencies?.basis.map((comp) => (
                                <li key={comp.id} className="py-3 flex items-center justify-between group">
                                  <div>
                                    <span className="text-sm text-gray-900">{comp.name}</span>
                                    {comp.beschrijving && (
                                      <p className="text-xs text-gray-500 mt-0.5 truncate max-w-md">
                                        {comp.beschrijving}
                                      </p>
                                    )}
                                  </div>
                                  <button
                                    onClick={() => handleRemoveCompetency(comp.id)}
                                    disabled={removeCompetency.isPending}
                                    className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-600 transition-opacity"
                                    title="Verwijderen"
                                  >
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-sm text-gray-400 italic">Geen basiscompetenties toegewezen.</p>
                          )}
                        </div>

                        {/* Vak Competencies (via tasks) */}
                        <div>
                          <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                            Vakcompetenties ({employeeCompetencies?.vak.length ?? 0})
                            <span className="text-xs text-gray-400 font-normal">(via taken)</span>
                          </h3>
                          {(employeeCompetencies?.vak.length ?? 0) > 0 ? (
                            <ul className="divide-y divide-gray-200">
                              {employeeCompetencies?.vak.map((comp) => (
                                <li key={comp.id} className="py-3 flex items-center justify-between">
                                  <div>
                                    <span className="text-sm text-gray-900">{comp.name}</span>
                                    {comp.beschrijving && (
                                      <p className="text-xs text-gray-500 mt-0.5 truncate max-w-md">
                                        {comp.beschrijving}
                                      </p>
                                    )}
                                  </div>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-sm text-gray-400 italic">Geen vakcompetenties (via toegewezen taken).</p>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Stats */}
            <div
              data-testid="employee-statistieken"
              className="bg-white shadow-sm border border-gray-200 rounded-lg p-6"
            >
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Statistieken
              </h2>
              {/*
                Eén bron. Deze twee getallen kwamen uit `getEmployeeById`
                terwijl de tabbadge en het puntentotaal onder de takenlijst uit
                `getEmployeeTasks` komen -- twee bronnen voor hetzelfde getal is
                precies de oorzaak die dit onderwerp opruimt. Ze lezen nu
                dezelfde payload; `employee.*` blijft alleen als terugval
                zolang die payload nog laadt.
                Spec: _bmad-output/implementation-artifacts/spec-weergave-actief-puntentotaal.md
              */}
              <dl className="space-y-4">
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Toegewezen taken</dt>
                  <dd className="text-sm font-medium text-gray-900">
                    {activeAssignedTasks?.length ?? employee.tasksCount}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Totaal punten</dt>
                  <dd className="text-sm font-medium text-gray-900">
                    {assignedTasks?.totalWeging ??
                      employee.tasks.reduce((sum, t) => sum + t.weging, 0)}{' '}
                    punten
                  </dd>
                </div>
              </dl>
            </div>

            {/* Metadata */}
            <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Systeem Info
              </h2>
              <dl className="space-y-3">
                <div>
                  <dt className="text-xs text-gray-500">Aangemaakt</dt>
                  <dd className="text-sm text-gray-900">
                    {new Date(employee.createdAt).toLocaleDateString('nl-NL')}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Laatst gewijzigd</dt>
                  <dd className="text-sm text-gray-900">
                    {new Date(employee.updatedAt).toLocaleDateString('nl-NL')}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">User ID</dt>
                  <dd className="text-sm text-gray-900">{employee.userId}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Medewerker verwijderen
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Weet u zeker dat u het profiel van <strong>{employee.name}</strong> wilt verwijderen?
                        Dit verwijdert ook alle toegewezen taken. Deze actie kan niet ongedaan worden gemaakt.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleteEmployee.isPending}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {deleteEmployee.isPending ? 'Verwijderen...' : 'Verwijderen'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={deleteEmployee.isPending}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                  >
                    Annuleren
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Task Assignment Modal */}
        {showTaskAssignment && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                aria-hidden="true"
                onClick={() => {
                  setShowTaskAssignment(false);
                  setTaskFilter('');
                }}
              />
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
              <div className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-xl sm:w-full sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Taak Toewijzen
                  </h3>
                  <button
                    onClick={() => {
                      setShowTaskAssignment(false);
                      setTaskFilter('');
                    }}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {!availableTasks || availableTasks.length === 0 ? (
                  <div className="text-center py-6">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <p className="mt-2 text-sm text-gray-500">
                      Alle beschikbare taken zijn al toegewezen.
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-gray-500 mb-4">
                      Selecteer een taak om toe te wijzen aan {employee.name}
                    </p>
                    {/* Filter input */}
                    <div className="mb-4">
                      <div className="input-icon-wrapper">
                        <svg className="input-icon h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                          type="text"
                          value={taskFilter}
                          onChange={(e) => setTaskFilter(e.target.value)}
                          placeholder="Zoek op naam of omschrijving..."
                          className="block w-full pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                        {taskFilter && (
                          <button
                            onClick={() => setTaskFilter('')}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          >
                            <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      <ul className="divide-y divide-gray-200">
                        {availableTasks
                          .filter((task) => {
                            if (!taskFilter) return true;
                            return (
                              matchesSearch(task.name, taskFilter) ||
                              matchesSearch(task.beschrijving ?? '', taskFilter)
                            );
                          })
                          .map((task) => (
                          <li
                            key={task.id}
                            className="py-3 flex items-center justify-between hover:bg-gray-50 px-2 -mx-2 rounded"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900">{task.name}</p>
                              {task.beschrijving && (
                                <p className="text-xs text-gray-500 mt-0.5 truncate">
                                  {task.beschrijving}
                                </p>
                              )}
                              <div className="flex items-center mt-1">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                  {task.weging} punten
                                </span>
                              </div>
                            </div>
                            <button
                              onClick={() => handleAssignTask(task.id)}
                              disabled={assignTask.isPending}
                              className="ml-4 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                            >
                              {assignTask.isPending ? (
                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                              ) : (
                                'Toewijzen'
                              )}
                            </button>
                          </li>
                        ))}
                      </ul>
                      {taskFilter && availableTasks.filter((task) => {
                        return (
                          matchesSearch(task.name, taskFilter) ||
                          matchesSearch(task.beschrijving ?? '', taskFilter)
                        );
                      }).length === 0 && (
                        <p className="text-sm text-gray-500 text-center py-4">
                          Geen taken gevonden voor "{taskFilter}"
                        </p>
                      )}
                    </div>
                  </>
                )}

                <div className="mt-5 sm:mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowTaskAssignment(false);
                      setTaskFilter('');
                    }}
                    className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm"
                  >
                    Sluiten
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Competency Assignment Modal */}
        {showCompetencyAssignment && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                aria-hidden="true"
                onClick={() => {
                  setShowCompetencyAssignment(false);
                  setCompetencyFilter('');
                }}
              />
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
              <div className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-xl sm:w-full sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Basiscompetentie Toewijzen
                  </h3>
                  <button
                    onClick={() => {
                      setShowCompetencyAssignment(false);
                      setCompetencyFilter('');
                    }}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {unassignedBasisCompetencies.length === 0 ? (
                  <div className="text-center py-6">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="mt-2 text-sm text-gray-500">
                      Alle basiscompetenties zijn al toegewezen.
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-gray-500 mb-4">
                      Selecteer een basiscompetentie om toe te wijzen aan {employee.name}
                    </p>
                    {/* Filter input */}
                    <div className="mb-4">
                      <div className="input-icon-wrapper">
                        <svg className="input-icon h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                          type="text"
                          value={competencyFilter}
                          onChange={(e) => setCompetencyFilter(e.target.value)}
                          placeholder="Zoek op naam of omschrijving..."
                          className="block w-full pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                        />
                        {competencyFilter && (
                          <button
                            onClick={() => setCompetencyFilter('')}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          >
                            <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      <ul className="divide-y divide-gray-200">
                        {unassignedBasisCompetencies
                          .filter((comp) => {
                            if (!competencyFilter) return true;
                            return (
                              matchesSearch(comp.name, competencyFilter) ||
                              matchesSearch(comp.beschrijving ?? '', competencyFilter)
                            );
                          })
                          .map((comp) => (
                          <li
                            key={comp.id}
                            className="py-3 flex items-center justify-between hover:bg-gray-50 px-2 -mx-2 rounded"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900">{comp.name}</p>
                              {comp.beschrijving && (
                                <p className="text-xs text-gray-500 mt-0.5 truncate">
                                  {comp.beschrijving}
                                </p>
                              )}
                            </div>
                            <button
                              onClick={() => handleAssignCompetency(comp.id)}
                              disabled={assignCompetency.isPending}
                              className="ml-4 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                            >
                              {assignCompetency.isPending ? (
                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                              ) : (
                                'Toewijzen'
                              )}
                            </button>
                          </li>
                        ))}
                      </ul>
                      {competencyFilter && unassignedBasisCompetencies.filter((comp) => {
                        return (
                          matchesSearch(comp.name, competencyFilter) ||
                          matchesSearch(comp.beschrijving ?? '', competencyFilter)
                        );
                      }).length === 0 && (
                        <p className="text-sm text-gray-500 text-center py-4">
                          Geen competenties gevonden voor "{competencyFilter}"
                        </p>
                      )}
                    </div>
                  </>
                )}

                <div className="mt-5 sm:mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCompetencyAssignment(false);
                      setCompetencyFilter('');
                    }}
                    className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 sm:text-sm"
                  >
                    Sluiten
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </Layout>
  );
}
